import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, Link } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import axiosInstance from "axiosInstance";
// MUI Components
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from "@mui/material";
import { Block, Edit, Delete } from "@mui/icons-material";

export default function PostList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);

  const { register, handleSubmit, watch, setValue } = useForm({
    defaultValues: { 
      title: '',
      id: '',
      category_id: ''
    }
  });

  // Fetch posts and categories
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.post("/6tduyrduytdfuytcfutyrfc/posts/list", { 
          page, 
          ...query 
        });
        setData(response.data?.data);
        
        // Extract categories from response or set default
        if (response.data?.data?.categoriesAll) {
          setCategories(response.data.data.categoriesAll);
        }
      } catch (error) {
        console.error('Error fetching posts:', error);
        alert('Error loading posts');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, query]);

  const handleSearch = (values) => {
    setQuery(values);
    setPage(1); // Reset to first page when searching
  };
  const handleAddPost = () => {
    navigate("/blogs/add");
  };
  
  const handleEditPost = (id) => {
    navigate(`/blogs/edit/${id}`);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    setDeleteLoading(true);
    try {
      await axiosInstance.post("/6tduyrduytdfuytcfutyrfc/posts/delete", { id: deleteId });
      alert("Post deleted successfully");
      setDeleteDialogOpen(false);
      setDeleteId(null);
      
      // Refetch data
      const response = await axiosInstance.post("/6tduyrduytdfuytcfutyrfc/posts/list", { 
        page, 
        ...query 
      });
      setData(response.data?.data);
    } catch (error) {
      console.error('Error deleting post:', error);
      alert("Error deleting post");
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDeleteDialog = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  };

  if (loading && !data) {
    return (
      <DashboardLayout>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading posts...</Typography>
      </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <Box sx={{ p: 2 }}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h3" textAlign="center">
            Post List
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardHeader
          title={
            <Stack direction={"row"} justifyContent={"space-between"} alignItems="center">
              <Typography variant="subtitle1">
                Total Count: {data?.pagination?.total || 0}
              </Typography>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleAddPost}
              >
                ADD POST
              </Button>
            </Stack>
          }
        />
        <CardContent>
          <form onSubmit={handleSubmit(handleSearch)}>
            <Grid container spacing={2} justifyContent="center" alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  {...register("category_id")}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  <MenuItem value="0">News</MenuItem>
                  {categories.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Post Code"
                  {...register("id")}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Title"
                  {...register("title")}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  fullWidth
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      <Paper>
        <TableContainer>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Post Code</TableCell>
                <TableCell>Image</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.posts?.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>
                    {row.main_image_url ? (
                      <img 
                        src={`https://api.fruitsweb3.xyz/images/${row.main_image_url}`} 
                        alt={row.title}
                        style={{ width: 100, height: 80, objectFit: 'cover' }}
                      />
                    ) : (
                      'No Image'
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {row.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.category_id !== 0 ? row.category?.name : "News"}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <IconButton 
                        color="secondary"
                        onClick={() => handleEditPost(row.id)}
                      >
                        <Edit />
                      </IconButton>

                      <IconButton
                        onClick={() => openDeleteDialog(row.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {data?.posts?.length === 0 && (
          <Box py={4} textAlign="center">
            <Typography variant="h6" color="textSecondary">
              No posts found
            </Typography>
          </Box>
        )}

        {data?.pagination?.last_page > 1 && (
          <Box py={2.5} justifyContent={"center"} display={"flex"}>
            <Pagination
              onChange={(e, value) => setPage(value)}
              page={page}
              count={data.pagination.last_page}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this post? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </DashboardLayout>
  );
}